import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MarkdownPrimitive } from '@almanac/catalog';
import { ApiError, fetchPublicResearch, type PublicResearchItem } from '../api/client';
import { formatTimestampDate } from '../lib/format';
import {
  MIN_POSTER_HEIGHT,
  framedPosterHeight,
  POSTER_HEIGHT_MESSAGE,
  POSTER_SANDBOX,
  usePosterColorScheme,
  withPosterMeasureScript,
} from '../lib/poster';

/** Fetch state for the public share route. */
type ShareState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; item: PublicResearchItem };

/**
 * Unauthenticated share page for visibility='public' research, served at
 * /p/:slug outside RequireAuth. Content-only: no app chrome, comments, or
 * owner metadata. The poster renders in the same sandboxed, height-synced
 * iframe as the internal item page.
 */
export function PublicResearchPage() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<ShareState>({ status: 'loading' });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [posterHeight, setPosterHeight] = useState(MIN_POSTER_HEIGHT);
  const posterColorScheme = usePosterColorScheme();

  useEffect(() => {
    setState({ status: 'loading' });
    fetchPublicResearch(slug)
      .then((item) => setState({ status: 'loaded', item }))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) {
          setState({ status: 'error', message: 'This research is not available.' });
          return;
        }
        setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
      });
  }, [slug]);

  const posterSrcDoc = useMemo(() => {
    if (state.status !== 'loaded' || !state.item.body_html) return null;
    return withPosterMeasureScript(state.item.body_html);
  }, [state]);

  // Same height-report contract as ResearchItemPage: trust only this iframe's
  // contentWindow and read only the numeric height.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const payload = event.data;
      if (typeof payload !== 'object' || payload === null) return;
      if ((payload as { type?: unknown }).type !== POSTER_HEIGHT_MESSAGE) return;
      const height = framedPosterHeight((payload as { height?: unknown }).height);
      if (height === null) return;
      setPosterHeight(height);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="fixed inset-0 grid place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="fixed inset-0 grid place-items-center px-4 text-center text-sm text-muted-foreground">
        {state.message}
      </div>
    );
  }

  const { item } = state;
  return (
    <main className="mx-auto max-w-3xl space-y-4 px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
        {item.title}
      </h1>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {item.published_at ? <span>{formatTimestampDate(item.published_at)}</span> : null}
        {item.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"
          >
            {tag}
          </span>
        ))}
      </div>

      {posterSrcDoc ? (
        <>
          {item.body_md ? (
            <details open>
              <summary className="cursor-pointer text-sm text-muted-foreground">
                Text version
              </summary>
              <div className="mt-2 rounded-xl border border-border bg-card p-4">
                <MarkdownPrimitive body={item.body_md} />
              </div>
            </details>
          ) : null}
          {/* Full-bleed breakout matching the internal item page. */}
          <div className="relative left-1/2 flex w-[min(100vw-2rem,96rem)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card">
            {/*
              SECURITY (load-bearing): agent-authored poster in a sandboxed
              iframe WITHOUT allow-same-origin. NEVER add allow-same-origin.
              (See web/src/lib/poster.ts.)
            */}
            <iframe
              ref={iframeRef}
              title={item.title}
              srcDoc={posterSrcDoc}
              sandbox={POSTER_SANDBOX}
              className="block w-full"
              style={{ height: `${posterHeight}px`, colorScheme: posterColorScheme }}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4">
          <MarkdownPrimitive body={item.body_md} />
        </div>
      )}
    </main>
  );
}
